/**
 * Technical Indicators Utility Functions
 */

// Simple Moving Average (SMA)
export const calculateSMA = (data: number[], period: number): { time: string; value: number }[] => {
  const sma = [];
  for (let i = period - 1; i < data.length; i++) {
    const sum = data.slice(i - period + 1, i + 1).reduce((a, b) => a + b, 0);
    sma.push(sum / period);
  }
  return sma.map((val, index) => ({
    time: '', // Placeholder, will be mapped later
    value: val,
  }));
};

// Exponential Moving Average (EMA)
export const calculateEMA = (data: number[], period: number): number[] => {
  const k = 2 / (period + 1);
  const emaArray = [data[0]];
  for (let i = 1; i < data.length; i++) {
    emaArray.push(data[i] * k + emaArray[i - 1] * (1 - k));
  }
  return emaArray;
};

// Relative Strength Index (RSI)
export const calculateRSI = (data: number[], period: number = 14): number[] => {
  if (data.length < period) return [];

  let gains = 0;
  let losses = 0;

  // First average gain/loss
  for (let i = 1; i <= period; i++) {
    const change = data[i] - data[i - 1];
    if (change > 0) gains += change;
    else losses -= change;
  }

  let avgGain = gains / period;
  let avgLoss = losses / period;

  const rsiArray = [];

  /**
   * RSI de Wilder, avec les deux cas limites que la formule brute ne couvre pas.
   *
   * ⚠️ `rs = avgGain / avgLoss` DONNE NaN QUAND LES DEUX SONT NULS. C'est le cas dès
   * qu'une valeur ne bouge pas pendant toute la fenêtre — situation courante à la BRVM,
   * où la liquidité est faible : SAFCA compte jusqu'à 46 séances consécutives à clôture
   * identique. Le NaN se propageait jusqu'à `setData`, et lightweight-charts rejetait
   * alors la série ENTIÈRE avec « Line series item data value must be between... ».
   *
   *   avgLoss = 0 et avgGain > 0  ->  100  (que des hausses, convention de Wilder)
   *   avgLoss = 0 et avgGain = 0  ->   50  (aucun mouvement : neutre, pas « suracheté »)
   *
   * Rendre 100 pour un titre qui n'a pas bougé serait un contresens : ce n'est pas un
   * excès d'achat, c'est une absence de transaction.
   */
  const rsiDepuis = (gain: number, perte: number): number => {
    if (perte === 0) return gain === 0 ? 50 : 100;
    return 100 - 100 / (1 + gain / perte);
  };

  // Initial RSI
  rsiArray.push(rsiDepuis(avgGain, avgLoss));

  // Subsequent RSI
  for (let i = period + 1; i < data.length; i++) {
    const change = data[i] - data[i - 1];
    const gain = change > 0 ? change : 0;
    const loss = change < 0 ? -change : 0;

    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;

    rsiArray.push(rsiDepuis(avgGain, avgLoss));
  }

  // Pad with nulls/zeros for alignment if needed, or return aligned array
  // For simplicity, we return the calculated values. The consumer must align dates.
  return rsiArray;
};

// MACD
export const calculateMACD = (
  data: number[],
  fastPeriod: number = 12,
  slowPeriod: number = 26,
  signalPeriod: number = 9
) => {
  const fastEMA = calculateEMA(data, fastPeriod);
  const slowEMA = calculateEMA(data, slowPeriod);

  // MACD Line = Fast EMA - Slow EMA
  const macdLine = fastEMA.map((val, i) => val - slowEMA[i]);

  // Signal Line = EMA of MACD Line
  const signalLine = calculateEMA(macdLine, signalPeriod);

  // Histogram = MACD Line - Signal Line
  const histogram = macdLine.map((val, i) => val - signalLine[i]);

  return { macdLine, signalLine, histogram };
};

// Bollinger Bands
export const calculateBollingerBands = (
  data: number[],
  period: number = 20,
  multiplier: number = 2
) => {
  const sma = [];
  const upper = [];
  const lower = [];

  for (let i = period - 1; i < data.length; i++) {
    const slice = data.slice(i - period + 1, i + 1);
    const sum = slice.reduce((a, b) => a + b, 0);
    const mean = sum / period;

    const squaredDiffs = slice.map((val) => Math.pow(val - mean, 2));
    const variance = squaredDiffs.reduce((a, b) => a + b, 0) / period;
    const stdDev = Math.sqrt(variance);

    sma.push(mean);
    upper.push(mean + multiplier * stdDev);
    lower.push(mean - multiplier * stdDev);
  }

  return { sma, upper, lower };
};

// Market Interpretation
export function interpretMarket(data: any[]) {
  if (!data || data.length < 20) {
    return 'Pas assez de données pour analyser le marché.';
  }

  // ----------------------------------------------
  // UTILS
  // ----------------------------------------------
  // Data structure: { time, open, high, low, close, volume } or similar
  // We need to ensure we access the correct properties.
  // Assuming data is sorted oldest to newest (standard for charts)

  const last = data[data.length - 1];
  const prev = data[data.length - 2];

  const lastClose = Number(last.close || last.cours_cloture);
  const prevClose = Number(prev.close || prev.cours_cloture);
  const lastOpen = Number(last.open || last.cours_ouverture);
  const lastHigh = Number(last.high || last.cours_plus_haut || lastClose); // Fallback
  const lastLow = Number(last.low || last.cours_plus_bas || lastClose); // Fallback
  const lastVolume = Number(last.volume || last.volume_echange || 0);

  // Variation %
  const priceChange = ((lastClose - prevClose) / prevClose) * 100;

  // Volume moyen
  const avgVolume =
    data.slice(-20).reduce((acc, d) => acc + Number(d.volume || d.volume_echange || 0), 0) / 20;

  // Range des 30 derniers jours
  const recent = data.slice(-30);
  const highest = Math.max(
    ...recent.map((d) => Number(d.high || d.cours_plus_haut || d.close || d.cours_cloture))
  );
  const lowest = Math.min(
    ...recent.map((d) => Number(d.low || d.cours_plus_bas || d.close || d.cours_cloture))
  );
  const rangeAmplitude = ((highest - lowest) / lowest) * 100;

  // Construction du texte final
  const analysis = [];

  // ----------------------------------------------
  // 1. TENDANCE (basée sur SMA 20 et SMA 50)
  // ----------------------------------------------
  const sma = (period: number) => {
    const slice = data.slice(-period);
    return slice.reduce((acc, d) => acc + Number(d.close || d.cours_cloture), 0) / period;
  };

  const sma20 = sma(20);
  const sma50 = sma(50);

  if (sma20 > sma50 * 1.01) {
    analysis.push('La tendance court terme est haussière.');
  } else if (sma20 < sma50 * 0.99) {
    analysis.push('La tendance court terme est baissière.');
  } else {
    analysis.push('Le marché évolue dans une tendance neutre.');
  }

  // ----------------------------------------------
  // 2. Prix : mouvement récent
  // ----------------------------------------------
  if (priceChange > 2) {
    analysis.push('Le prix progresse fortement aujourd’hui.');
  } else if (priceChange < -2) {
    analysis.push('Le prix recule nettement aujourd’hui.');
  } else if (Math.abs(priceChange) < 0.2) {
    analysis.push('Le prix est quasiment stable.');
  }

  // ----------------------------------------------
  // 3. Volume
  // ----------------------------------------------
  if (lastVolume === 0) {
    analysis.push('Aucune donnée de volume disponible.');
  } else {
    if (lastVolume > avgVolume * 2) {
      analysis.push('Le volume est exceptionnellement élevé.');
    } else if (lastVolume < avgVolume * 0.3) {
      analysis.push('La liquidité est très faible.');
    } else {
      analysis.push('Le volume est proche de la moyenne.');
    }
  }

  // ----------------------------------------------
  // 4. Volatilité / Range
  // ----------------------------------------------
  if (rangeAmplitude < 3) {
    analysis.push('Le marché est dans un range étroit depuis plusieurs semaines.');
  } else if (rangeAmplitude > 15) {
    analysis.push('Le marché montre une forte volatilité historique récente.');
  }

  // ----------------------------------------------
  // 5. Breakouts (cassures)
  // ----------------------------------------------
  if (lastClose > highest * 0.995) {
    analysis.push('Le prix teste une zone de résistance importante.');
  } else if (lastClose < lowest * 1.005) {
    analysis.push('Le prix teste une zone de support majeure.');
  }

  // ----------------------------------------------
  // 6. Bougie du jour (pattern simple)
  // ----------------------------------------------
  const candleBody = Math.abs(lastClose - lastOpen);
  const candleRange = lastHigh - lastLow;

  if (candleRange > 0 && candleBody / candleRange < 0.2) {
    analysis.push('La bougie du jour reflète une indécision du marché.');
  }

  // ----------------------------------------------
  // 7. Synthèse finale
  // ----------------------------------------------
  return analysis.join(' ');
}

interface DividendAnalysisParams {
  name: string;
  dividends: { year: number; amount: number }[];
  lastPrice: number;
}

export function analyzeDividends({ name, dividends, lastPrice }: DividendAnalysisParams): string {
  if (!dividends || dividends.length === 0) {
    return `La société ${name} n’a versé aucun dividende sur la période analysée.`;
  }

  const amounts = dividends.map((d) => d.amount);
  const years = dividends.map((d) => d.year);

  // Basics
  const lastDividend = amounts[amounts.length - 1];
  const avgDividend = amounts.reduce((a, b) => a + b, 0) / amounts.length;

  // Dividend growth (CAGR)
  const first = amounts[0];
  const last = amounts[amounts.length - 1];
  const n = dividends.length - 1;

  // first/lastPrice à 0 donnerait Infinity/NaN (division par zéro) — garde défensive, même si les
  // lignes statut='AUCUN' (montant=0) sont normalement déjà filtrées avant d'arriver ici.
  const cagr = n > 0 && first > 0 ? ((last / first) ** (1 / n) - 1) * 100 : 0;

  // Dividend Yield actuel
  const dividendYield = lastPrice > 0 ? ((lastDividend / lastPrice) * 100).toFixed(2) : 'N/A';

  // Stability: nombre d'années sans baisse du dividende
  let increases = 0;
  for (let i = 1; i < amounts.length; i++) {
    if (amounts[i] >= amounts[i - 1]) increases++;
  }

  const stabilityScore = ((increases / (amounts.length - 1)) * 100).toFixed(0);

  // Génération du commentaire
  return `
La société ${name} verse en moyenne un dividende de ${avgDividend.toFixed(0)} FCFA. 
Le dernier dividende connu est de ${lastDividend} FCFA, soit un rendement de ${dividendYield}% par rapport au cours actuel.

Sur la période ${years[0]}–${years[years.length - 1]}, le dividende a ${cagr >= 0
      ? `augmenté en moyenne de ${cagr.toFixed(2)}% par an`
      : `diminué en moyenne de ${Math.abs(cagr).toFixed(2)}% par an`
    }.

Le niveau de stabilité, estimé à ${stabilityScore}%, indique ${Number(stabilityScore) > 70
      ? 'une politique de dividende plutôt régulière.'
      : Number(stabilityScore) > 40
        ? 'une politique de dividende variable.'
        : 'une forte variabilité du dividende.'
    }
  `.trim();
}
